import { AxiosError } from "axios";
import { all, call, put, StrictEffect, takeEvery } from "redux-saga/effects";

import { DataType } from "@/jenesys-types/data/enums";
import { AppError } from "@/jenesys-types/errors";
import { EntityRouteListIds } from "@/jenesys-types/list";

import { performDataAction } from "@/services/api";
import { refreshList } from "@/store/crud/list";
import { fetchAdjacentDocumentRequest } from "@/store/features/transactions/entityNavigation/entityNavigation";
import { toastError, toastSuccess } from "@/util/helpers";
import { performanceMonitor } from "@/utils/performance";

import { loadUpdateItem } from "@/store/crud/update";
import {
  bulkDelete,
  BulkDeleteAction,
  bulkDeleteFailure,
  bulkDeleteSuccess,
  bulkPublishing,
  BulkPublishingAction,
  bulkPublishingFailure,
  bulkPublishingSuccess,
  fetchBulkPublishing,
  FetchBulkPublishingAction,
  fetchBulkPublishingFailure,
  fetchBulkPublishingSuccess,
  manualPublish,
  ManualPublishAction,
  manualPublishFailure,
  manualPublishSuccess,
  validateBulkPublishing,
  ValidateBulkPublishingAction,
  validateBulkPublishingFailure,
  validateBulkPublishingSuccess
} from "./bulkPublishing";

function* fetchBulkPublishingSaga(
  action: FetchBulkPublishingAction
): Generator<StrictEffect, void, any> {
  const { clientId } = action.payload;

  try {
    const response = yield call(
      performDataAction,
      DataType.BulkPublishing,
      "fetch",
      { clientId }
    );
    yield put(fetchBulkPublishingSuccess(response));
  } catch (error) {
    const appError = error as AppError;
    yield put(fetchBulkPublishingFailure(appError));
  }
}

function* validateBulkPublishingSaga(
  action: ValidateBulkPublishingAction
): Generator<StrictEffect, void, any> {
  const { clientId, documentsIds } = action.payload;

  try {
    const response = yield call(
      performDataAction,
      DataType.BulkPublishing,
      "validate",
      { clientId },
      {},
      { documents_ids: documentsIds }
    );

    if (response.invalid_documents?.length) {
      toastError(
        `Validation failed for ${response.invalid_documents.length} document(s)`,
        "Validation Error",
        `This means that some documents do not contain all the required information the Accounting Platform needs for publishing.
              Please review each document and make the necessary edits, save and try bulk publish again.
              
              Visit the Help Centre to learn more about the required information fields.`
      );
      yield put(
        validateBulkPublishingFailure({
          message: `Validation failed for ${response.invalid_documents.length} document(s)`,
          code: "N/A"
        })
      );
      return;
    }

    yield put(validateBulkPublishingSuccess());
    toastSuccess("Bulk publish validated successfully");
  } catch (error) {
    let appError = { message: "An unknown error occurred", code: "UNKNOWN" };

    if (error instanceof AxiosError) {
      appError = {
        message: error.message,
        code: error.code || "UNKNOWN"
      };
    } else if (error instanceof Error) {
      appError = { message: error.message, code: "GENERAL_ERROR" };
    }
    yield put(validateBulkPublishingFailure(appError));
    toastError(appError.message);
  }
}

function* bulkPublishingSaga(
  action: BulkPublishingAction
): Generator<StrictEffect, void, any> {
  const { clientId, documentsIds, bulkPublishBatchName } = action.payload;
  try {
    yield call(
      performDataAction,
      DataType.BulkPublishing,
      "publish",
      { clientId },
      {},
      { documents_ids: documentsIds, name: bulkPublishBatchName }
    );

    yield put(bulkPublishingSuccess());
    toastSuccess("Documents sent for publish successfully");
  } catch (error) {
    const appError = error as AppError;
    yield put(bulkPublishingFailure(appError));
  }
}

function* bulkDeleteSaga(
  action: BulkDeleteAction
): Generator<StrictEffect, void, any> {
  const { clientId, documentsIds, reason } = action.payload;

  try {
    // Pass documents_ids as an array which will be handled correctly by the API layer for DELETE requests
    const queryParams = {
      reason,
      documents_ids: documentsIds
    };

    yield call(
      performDataAction,
      DataType.Transaction,
      "bulkDelete",
      { clientId },
      queryParams,
      {}
    );

    yield put(bulkDeleteSuccess());

    // Refresh transactions list after successful delete
    yield put(
      refreshList(DataType.Transaction, EntityRouteListIds.Transactions)
    );

    toastSuccess("Documents deleted successfully");
  } catch (error) {
    let appError = { message: "An unknown error occurred", code: "UNKNOWN" };

    if (error instanceof AxiosError) {
      appError = {
        message: error.message,
        code: error.code || "UNKNOWN"
      };
    } else if (error instanceof Error) {
      appError = { message: error.message, code: "GENERAL_ERROR" };
    }
    yield put(bulkDeleteFailure(appError));
    toastError(appError.message);
  }
}

function* manualPublishSaga(
  action: ManualPublishAction
): Generator<StrictEffect, void, any> {
  const { documents_ids, clientId } = action.payload;
  const metricName = "Saga.manualPublishSaga";
  const metricId = performanceMonitor.start(metricName, {
    sagaInstance: `${DataType.Transaction}-${documents_ids.join(",")}`,
    ...action.payload
  });

  try {
    yield call(
      performDataAction,
      DataType.BulkPublishing,
      "manualPublish",
      { clientId },
      { documents_ids },
      {}
    );

    yield put(manualPublishSuccess());
    yield all(
        documents_ids.map((id) =>
            put(loadUpdateItem(DataType.Transaction, id, {}))
        )
    );
    toastSuccess("Documents published successfully");

    const lastDocumentId = documents_ids[documents_ids.length - 1];
    const status = localStorage.getItem("lastDocumentNavigationStatus") || "REVIEWED";
    yield put(
      fetchAdjacentDocumentRequest(
        clientId,
        lastDocumentId,
        "next",
        status,
        {
          onFinalDocument: {
            redirectTo: `/client/${clientId}/transactions/management`
          },
          onFailure: {
            redirectTo: `/client/${clientId}/transactions/management`
          }
        }
      )
    );
  } catch (error) {
    let appError = { message: "An unknown error occurred", code: "UNKNOWN" };

    if (error instanceof AxiosError) {
      appError = {
        message: error.message,
        code: error.code || "UNKNOWN"
      };
    } else if (error instanceof Error) {
      appError = { message: error.message, code: "GENERAL_ERROR" };
    }
    yield put(manualPublishFailure(appError));
    toastError(appError.message);
  } finally {
    performanceMonitor.stop(metricId);
  }
}

export default function* BulkPublishingSaga() {
  yield takeEvery(fetchBulkPublishing.type, fetchBulkPublishingSaga);
  yield takeEvery(validateBulkPublishing.type, validateBulkPublishingSaga);
  yield takeEvery(bulkPublishing.type, bulkPublishingSaga);
  yield takeEvery(bulkDelete.type, bulkDeleteSaga);
  yield takeEvery(manualPublish.type, manualPublishSaga);
}
